import { Appointments } from './appointments';
import { ClassificationReview } from './quality';
import { useEffect, useRef, useState, type JSX } from 'react';
import { z } from 'zod';
import {
  generateUuidV7,
  renderMessageHtml,
  type BotScope,
} from '@cryptology-assist/domain';
import {
  botPath,
  conversationsSchema,
  detailSchema,
  idSchema,
  request,
  savedSchema,
  type ConversationDetail,
  type Session,
} from './api';
import { useRemote } from './hooks';
import {
  ActionSummary,
  ErrorNotice,
  Field,
  onSubmit,
  useMutation,
} from './components';
import { dateLabel, statusLabel, type Copy, type Language } from './copy';

const assigneesResponseSchema = z.object({
  members: z.array(
    z.object({
      account_id: z.string(),
      display_name: z.string(),
      role: z.string(),
    })
  ),
});

function humanReason(copy: Copy, detail: ConversationDetail): string {
  if (detail.handoffDecision?.decision.intent === 'human_help')
    return copy.humanReasonCustomer;
  if (detail.handoffDecision?.decision.needsHuman)
    return copy.humanReasonClassifier;
  return copy.humanReasonOperator;
}

function decisionSignals(
  copy: Copy,
  decision: NonNullable<ConversationDetail['decision']>['decision']
): string[] {
  return [
    ...(decision.paymentClaim ? [copy.payment_claim] : []),
    ...(decision.dispute ? [copy.dispute] : []),
    ...(decision.stop ? [copy.opt_out] : []),
    ...(decision.needsHuman ? [copy.human_hold] : []),
  ];
}

export function Inbox({
  scope,
  session,
  copy,
  language,
  canWrite,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
  canWrite: boolean;
}): JSX.Element {
  const transcript = useRef<HTMLDivElement>(null);
  const [before, setBefore] = useState<string | null>(null),
    [selected, setSelected] = useState<string | null>(() =>
      new URLSearchParams(window.location.search).get('conversationId')
    ),
    [text, setText] = useState(''),
    [attention, setAttention] = useState(false),
    [mine, setMine] = useState(false),
    [search, setSearch] = useState(''),
    [submittedSearch, setSubmittedSearch] = useState(''),
    [assignee, setAssignee] = useState(session.accountId),
    [deadline, setDeadline] = useState('');
  const assignees = useRemote(
    canWrite ? botPath(scope, 'conversations/assignees') : null,
    assigneesResponseSchema
  );
  const filters = new URLSearchParams({
    attention: String(attention),
    mine: String(mine),
    search: submittedSearch,
  });
  if (before) filters.set('before', before);
  const list = useRemote(
    botPath(scope, `conversations?${filters.toString()}`),
    conversationsSchema
  );
  const detail = useRemote(
    selected ? botPath(scope, `conversations/${selected}`) : null,
    detailSchema
  );
  const mutation = useMutation();
  useEffect(() => {
    const timer = window.setInterval(() => {
      list.reload();
      if (selected) detail.reload();
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [list.reload, detail.reload, selected]);
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    setSelected(
      query.get('companyId') === scope.companyId &&
        query.get('botId') === scope.botId
        ? query.get('conversationId')
        : null
    );
    setBefore(null);
    setText('');
    setSearch('');
    setSubmittedSearch('');
    setAttention(false);
    setMine(false);
    setDeadline('');
  }, [scope.companyId, scope.botId]);
  const updateMode = (mode: 'automatic' | 'human' | 'stopped') =>
    mutation.run(async () => {
      try {
        await request(
          botPath(scope, `conversations/${selected}/mode`),
          savedSchema,
          { csrf: session.csrf, body: { mode } }
        );
      } finally {
        // A blocked resume must still reveal the holds that caused it.
        detail.reload();
        list.reload();
      }
    });
  const retryEvent = (eventId: string) =>
    mutation.run(async () => {
      await request(
        botPath(scope, `conversations/${selected}/retry-event`),
        savedSchema,
        { csrf: session.csrf, body: { eventId } }
      );
      detail.reload();
      list.reload();
    });
  return (
    <section className="inbox-grid">
      <aside className="conversation-list">
        <div className="pane-header">
          <h2>{copy.inbox}</h2>
          <button className="quiet" onClick={list.reload}>
            {copy.refresh}
          </button>
        </div>
        <div className="list-filters">
          <form
            className="list-search"
            onSubmit={(event) => {
              event.preventDefault();
              setBefore(null);
              setSubmittedSearch(search.trim());
            }}
          >
            <input
              aria-label={copy.searchConversation}
              placeholder={copy.searchConversation}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <button type="submit">{copy.search}</button>
          </form>
          <div className="list-toggles">
            <label>
              <input
                type="checkbox"
                checked={attention}
                onChange={(event) => {
                  setAttention(event.target.checked);
                  setBefore(null);
                }}
              />
              {copy.needsAttention}
            </label>
            <label>
              <input
                type="checkbox"
                checked={mine}
                onChange={(event) => {
                  setMine(event.target.checked);
                  setBefore(null);
                }}
              />
              {copy.assignedToMe}
            </label>
            {before && (
              <button className="quiet" onClick={() => setBefore(null)}>
                {copy.firstPage}
              </button>
            )}
          </div>
        </div>
        <ErrorNotice error={list.error} copy={copy} onRetry={list.reload} />
        {list.loading && !list.data && (
          <p className="muted loading">{copy.loading}</p>
        )}
        {list.data?.conversations.length === 0 && (
          <p className="empty-copy">{copy.empty}</p>
        )}
        {list.data?.conversations.map((conversation) => (
          <button
            key={conversation.id}
            className={`conversation-row ${selected === conversation.id ? 'selected' : ''}`}
            onClick={() => {
              setSelected(conversation.id);
              setText('');
              const bounds = transcript.current?.getBoundingClientRect();
              if (
                bounds &&
                (bounds.top < 0 || bounds.top >= window.innerHeight)
              )
                transcript.current?.scrollIntoView({ block: 'start' });
            }}
          >
            <span className="row-top">
              <strong>
                {conversation.channel === 'web' ? copy.chat : copy.email}
              </strong>
              <span className={`state-dot ${conversation.automation_mode}`} />
            </span>
            {conversation.customerAddress && (
              <strong>{conversation.customerAddress}</strong>
            )}
            {conversation.subject && <span>{conversation.subject}</span>}
            {conversation.needs_attention && (
              <span className="badge">{copy.needsAttention}</span>
            )}
            {conversation.review_due_at && (
              <time
                className={
                  Date.parse(conversation.review_due_at) < Date.now()
                    ? 'error-text'
                    : ''
                }
              >
                {copy.reviewDeadline}:{' '}
                {dateLabel(conversation.review_due_at, language)}
              </time>
            )}
            <span>{statusLabel(copy, conversation.automation_mode)}</span>
            <time>{dateLabel(conversation.updated_at, language)}</time>
            {conversation.processing_error && (
              <small className="error-text">
                {conversation.processing_error}
              </small>
            )}
          </button>
        ))}
        {list.data?.conversations.length === 50 && (
          <button
            className="load-more"
            onClick={() =>
              setBefore(
                `${list.data!.conversations.at(-1)!.updated_at}|${list.data!.conversations.at(-1)!.id}`
              )
            }
          >
            {copy.loadMore}
          </button>
        )}
      </aside>
      <div className="transcript-pane" ref={transcript}>
        <div className="pane-header">
          <h2>{selected ? copy.inbox : copy.selectConversation}</h2>
          {detail.data && (
            <span className="badge">
              {statusLabel(copy, detail.data.conversation.automation_mode)}
            </span>
          )}
        </div>
        <ErrorNotice
          error={detail.error ?? mutation.error}
          copy={copy}
          onRetry={detail.reload}
        />
        {detail.loading && !detail.data && (
          <p className="muted loading">{copy.loading}</p>
        )}
        {!selected && (
          <div className="workspace-empty">
            <span className="conversation-mark" aria-hidden="true">
              ↗
            </span>
            <p>{copy.selectConversation}</p>
          </div>
        )}
        {detail.data && (
          <>
            <div className="message-list">
              {detail.data.messages.map((message) => (
                <article className={`message ${message.role}`} key={message.id}>
                  <header>
                    <strong>
                      {message.role === 'customer'
                        ? copy.customer
                        : message.role === 'assistant'
                          ? copy.assistant
                          : copy.operator}
                    </strong>
                    <time>{dateLabel(message.occurredAt, language)}</time>
                  </header>
                  {message.role === 'customer' ? (
                    <p>{message.text}</p>
                  ) : (
                    <div
                      className="formatted-message"
                      dangerouslySetInnerHTML={{
                        __html: renderMessageHtml(
                          message.text,
                          undefined,
                          language === 'es' ? 'es-PE' : 'en-US'
                        ),
                      }}
                    />
                  )}
                </article>
              ))}
            </div>
            {canWrite && (
              <form
                className="reply-composer"
                onSubmit={(event) =>
                  onSubmit(event, () =>
                    mutation.run(async () => {
                      if (!selected) return;
                      await request(
                        botPath(scope, `conversations/${selected}/reply`),
                        idSchema,
                        {
                          csrf: session.csrf,
                          body: {
                            requestId: generateUuidV7(),
                            text,
                            attachmentIds: [],
                          },
                        }
                      );
                      setText('');
                      detail.reload();
                    })
                  )
                }
              >
                <Field label={copy.reply}>
                  <textarea
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    rows={3}
                    maxLength={16_000}
                    required
                  />
                </Field>
                <button
                  className="primary"
                  disabled={mutation.busy || !text.trim()}
                >
                  {copy.send}
                </button>
              </form>
            )}
          </>
        )}
      </div>
      <aside className="inspector">
        {detail.data && (
          <>
            <div className="pane-header">
              <h2>{copy.actions}</h2>
            </div>
            <section className="inspector-section assistant-state">
              <div className="row-top">
                <h3>{copy.currentMode}</h3>
                <span className="badge">
                  {statusLabel(copy, detail.data.conversation.automation_mode)}
                </span>
              </div>
              {detail.data.conversation.automation_mode === 'human' && (
                <p className="small muted">{humanReason(copy, detail.data)}</p>
              )}
              {detail.data.conversation.automation_mode === 'stopped' && (
                <p className="small muted">{copy.stoppedReason}</p>
              )}
              {detail.data.conversation.automation_mode === 'human' &&
                detail.data.holds.some((hold) => hold.reason !== 'human') && (
                  <p className="small notice-inline">
                    {copy.resumeBlockedByHolds}
                  </p>
                )}
              <p className="identity-state">
                {detail.data.conversation.verified_subject &&
                detail.data.conversation.subject_verified_until &&
                Date.parse(detail.data.conversation.subject_verified_until) >
                  Date.now()
                  ? copy.verified
                  : copy.unverified}
              </p>
              {canWrite && (
                <div className="action-buttons">
                  <button
                    disabled={
                      mutation.busy ||
                      ['human', 'stopped'].includes(
                        detail.data.conversation.automation_mode
                      )
                    }
                    onClick={() => void updateMode('human')}
                  >
                    {copy.takeOver}
                  </button>
                  <button
                    disabled={
                      mutation.busy ||
                      ['automatic', 'stopped'].includes(
                        detail.data.conversation.automation_mode
                      )
                    }
                    onClick={() => void updateMode('automatic')}
                  >
                    {copy.resume}
                  </button>
                  <button
                    className="quiet danger"
                    disabled={
                      mutation.busy ||
                      detail.data.conversation.automation_mode === 'stopped'
                    }
                    onClick={() => void updateMode('stopped')}
                  >
                    {copy.stop}
                  </button>
                </div>
              )}
            </section>
            <section className="inspector-section">
              <h3>{copy.latestDecision}</h3>
              {detail.data.decision ? (
                <>
                  <p className="small muted">{copy.latestDecisionHelp}</p>
                  <dl className="decision-facts">
                    <div>
                      <dt>{copy.decisionIntent}</dt>
                      <dd>
                        {statusLabel(
                          copy,
                          `intent_${detail.data.decision.decision.intent}`
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>{copy.decisionSignals}</dt>
                      <dd>
                        {decisionSignals(copy, detail.data.decision.decision)
                          .length ? (
                          decisionSignals(
                            copy,
                            detail.data.decision.decision
                          ).map((signal) => (
                            <span className="badge" key={signal}>
                              {signal}
                            </span>
                          ))
                        ) : (
                          <span className="muted">
                            {copy.decisionNoSignals}
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>
                  <time>
                    {dateLabel(detail.data.decision.createdAt, language)}
                  </time>
                </>
              ) : (
                <p className="small muted">{copy.noDecision}</p>
              )}
            </section>
            {canWrite && (
              <form
                className="inspector-section"
                onSubmit={(event) => {
                  event.preventDefault();
                  void mutation.run(async () => {
                    await request(
                      botPath(scope, `conversations/${selected}/assign`),
                      savedSchema,
                      {
                        csrf: session.csrf,
                        body: {
                          assigneeId: assignee,
                          dueAt: new Date(deadline).toISOString(),
                        },
                      }
                    );
                    detail.reload();
                    list.reload();
                  });
                }}
              >
                <Field label={copy.assignee}>
                  <select
                    required
                    value={assignee}
                    onChange={(event) => setAssignee(event.target.value)}
                  >
                    {assignees.data?.members.map((member) => (
                      <option key={member.account_id} value={member.account_id}>
                        {member.display_name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={copy.reviewDeadline}>
                  <input
                    required
                    type="datetime-local"
                    value={deadline}
                    onChange={(event) => setDeadline(event.target.value)}
                  />
                </Field>
                <button
                  disabled={mutation.busy || !assignees.data}
                  type="submit"
                >
                  {copy.assign}
                </button>
                <ErrorNotice error={assignees.error} copy={copy} />
              </form>
            )}
            {detail.data.holds.length > 0 && (
              <section className="inspector-section">
                <h3>{copy.holds}</h3>
                {detail.data.holds.map((hold) => (
                  <div className="hold" key={hold.id}>
                    <strong>
                      {statusLabel(
                        copy,
                        hold.reason === 'human' ? 'human_hold' : hold.reason
                      )}
                    </strong>
                    <time>{dateLabel(hold.created_at, language)}</time>
                    {hold.sourceText && (
                      <p className="message-text">{hold.sourceText}</p>
                    )}
                    {hold.reason === 'pending_triage' && (
                      <>
                        <p className="small muted">
                          {copy.holdPendingTriageHelp}
                        </p>
                        {canWrite &&
                          detail.data!.failedEvents.some(
                            (event) => event.id === hold.source_event_id
                          ) && (
                            <button
                              disabled={mutation.busy}
                              onClick={() =>
                                void retryEvent(hold.source_event_id!)
                              }
                            >
                              {copy.retryClassification}
                            </button>
                          )}
                      </>
                    )}
                    {canWrite &&
                      [
                        'payment_claim',
                        'dispute',
                        'source_unavailable',
                      ].includes(hold.reason) && (
                        <HoldResolution
                          scope={scope}
                          conversationId={detail.data!.conversation.id}
                          holdId={hold.id}
                          reason={hold.reason}
                          sourceCaseId={hold.case_action_id}
                          session={session}
                          copy={copy}
                          reload={detail.reload}
                        />
                      )}
                  </div>
                ))}
              </section>
            )}
            {detail.data.failedEvents.map((event) => (
              <section className="inspector-section" key={event.id}>
                <p className="error-text">{event.error_code}</p>
                <time>{dateLabel(event.created_at, language)}</time>
                {canWrite && !event.classified && (
                  <ClassificationReview
                    item={{
                      id: event.id,
                      text: event.text,
                      conversationId: detail.data!.conversation.id,
                    }}
                    scope={scope}
                    session={session}
                    copy={copy}
                    reload={detail.reload}
                    reviewRoute={`conversations/${detail.data!.conversation.id}/review-event`}
                  />
                )}
                {canWrite && (
                  <button
                    disabled={mutation.busy}
                    onClick={() => void retryEvent(event.id)}
                  >
                    {copy.retry}
                  </button>
                )}
              </section>
            ))}
            {detail.data.outbox.map((item) => (
              <section className="inspector-section" key={item.id}>
                <div className="row-top">
                  <h3>{item.status === 'draft' ? copy.draft : copy.reply}</h3>
                  <span className="badge">
                    {statusLabel(copy, item.status)}
                  </span>
                </div>
                {item.status === 'draft' && canWrite ? (
                  <DraftEditor
                    id={item.id}
                    text={item.content.text}
                    scope={scope}
                    session={session}
                    copy={copy}
                    reload={detail.reload}
                  />
                ) : (
                  <p className="small prewrap">{item.content.text}</p>
                )}
                {item.error_code && (
                  <p
                    className={
                      item.status === 'cancelled' ? 'small muted' : 'error-text'
                    }
                  >
                    {statusLabel(copy, item.error_code)}
                  </p>
                )}
                {canWrite &&
                  ['failed', 'reconciliation_required'].includes(
                    item.status
                  ) && (
                    <button
                      disabled={mutation.busy}
                      onClick={() =>
                        void mutation.run(async () => {
                          await request(
                            botPath(scope, `outbox/${item.id}`),
                            savedSchema,
                            {
                              csrf: session.csrf,
                              body: { operation: 'recover' },
                            }
                          );
                          detail.reload();
                        })
                      }
                    >
                      {item.status === 'reconciliation_required'
                        ? copy.checkDelivery
                        : copy.retry}
                    </button>
                  )}
              </section>
            ))}
            <Appointments
              key={detail.data.conversation.id}
              scope={scope}
              conversationId={detail.data.conversation.id}
              session={session}
              copy={copy}
              language={language}
              canWrite={
                canWrite &&
                detail.data.conversation.automation_mode !== 'stopped'
              }
              onChanged={detail.reload}
            />
            {detail.data.actions.map((action) => (
              <section className="inspector-section" key={action.id}>
                <span className="badge">
                  {statusLabel(copy, action.status)}
                </span>
                <ActionSummary
                  action={action.request}
                  copy={copy}
                  language={language}
                />
                {action.result && (
                  <div>
                    <p className="small">
                      {copy.acknowledgement}: {action.result.reference}
                    </p>
                    {action.result.facts.length > 0 && (
                      <details>
                        <summary>{copy.authoritativeResults}</summary>
                        {action.result.facts.map((fact) => (
                          <article key={fact.id}>
                            <p className="small prewrap">{fact.content}</p>
                            <p className="small muted">
                              {fact.source} ·{' '}
                              {dateLabel(fact.observedAt, language)}
                            </p>
                          </article>
                        ))}
                      </details>
                    )}
                  </div>
                )}
                {action.errorCode && (
                  <p
                    className={
                      action.status === 'cancelled'
                        ? 'small muted'
                        : 'error-text'
                    }
                  >
                    {statusLabel(copy, action.errorCode)}
                  </p>
                )}
                {action.calendarBooking && (
                  <p className="small">
                    {copy.calendarConfirmed}:{' '}
                    {action.calendarBooking.providerBookingId}
                  </p>
                )}
                <p className="small">
                  {copy.recoveryAttempts}: {action.attempts}
                </p>
                {canWrite &&
                  (action.status === 'reconciliation_required' ||
                    (action.status === 'failed' &&
                      (action.recoveryKind === 'replay' ||
                        action.request.kind === 'book_appointment'))) && (
                    <button
                      disabled={mutation.busy}
                      onClick={() =>
                        void mutation.run(async () => {
                          await request(
                            botPath(
                              scope,
                              `conversations/${detail.data!.conversation.id}/actions/${action.id}/recover`
                            ),
                            savedSchema,
                            { csrf: session.csrf, body: {} }
                          );
                          detail.reload();
                        })
                      }
                    >
                      {action.recoveryKind === 'reconcile'
                        ? copy.reconcileAction
                        : copy.retryRead}
                    </button>
                  )}
              </section>
            ))}
            {detail.data.attachments.length > 0 && (
              <section className="inspector-section">
                <h3>{copy.attachments}</h3>
                {detail.data.attachments.map((file) => (
                  <div className="attachment" key={file.id}>
                    <strong>{file.filename}</strong>
                    <span>{statusLabel(copy, file.review_status)}</span>
                    <a
                      href={botPath(
                        scope,
                        `conversations/${selected}/attachments/${file.id}`
                      )}
                    >
                      {copy.download}
                    </a>
                    {canWrite && (
                      <>
                        <button
                          onClick={() =>
                            void mutation.run(async () => {
                              await request(
                                botPath(
                                  scope,
                                  `conversations/${selected}/attachments/${file.id}`
                                ),
                                savedSchema,
                                {
                                  csrf: session.csrf,
                                  body: { status: 'approved' },
                                }
                              );
                              detail.reload();
                            })
                          }
                        >
                          {copy.reviewApprove}
                        </button>
                        <button
                          onClick={() =>
                            void mutation.run(async () => {
                              await request(
                                botPath(
                                  scope,
                                  `conversations/${selected}/attachments/${file.id}`
                                ),
                                savedSchema,
                                {
                                  csrf: session.csrf,
                                  body: { status: 'rejected' },
                                }
                              );
                              detail.reload();
                            })
                          }
                        >
                          {copy.reviewReject}
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </section>
            )}
          </>
        )}
      </aside>
    </section>
  );
}

function DraftEditor({
  id,
  text,
  scope,
  session,
  copy,
  reload,
}: {
  id: string;
  text: string;
  scope: BotScope;
  session: Session;
  copy: Copy;
  reload: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState(text);
  const mutation = useMutation();
  return (
    <form
      onSubmit={(event) =>
        onSubmit(event, () =>
          mutation.run(async () => {
            await request(botPath(scope, `outbox/${id}`), savedSchema, {
              csrf: session.csrf,
              body: { operation: 'approve', text: draft },
            });
            reload();
          })
        )
      }
    >
      <textarea
        aria-label={copy.draft}
        value={draft}
        rows={7}
        maxLength={16_000}
        onChange={(event) => setDraft(event.target.value)}
        required
      />
      <ErrorNotice error={mutation.error} copy={copy} />
      <div className="action-buttons">
        <button className="primary" disabled={mutation.busy}>
          {copy.approve}
        </button>
        <button
          type="button"
          disabled={mutation.busy}
          onClick={() =>
            void mutation.run(async () => {
              await request(botPath(scope, `outbox/${id}`), savedSchema, {
                csrf: session.csrf,
                body: { operation: 'cancel' },
              });
              reload();
            })
          }
        >
          {copy.cancel}
        </button>
      </div>
    </form>
  );
}

function HoldResolution({
  scope,
  conversationId,
  holdId,
  reason,
  sourceCaseId,
  session,
  copy,
  reload,
}: {
  scope: BotScope;
  conversationId: string;
  holdId: string;
  reason: string;
  sourceCaseId: string | null;
  session: Session;
  copy: Copy;
  reload: () => void;
}): JSX.Element {
  const [reference, setReference] = useState(''),
    [note, setNote] = useState('');
  const [operation, setOperation] = useState('resolve');
  const mutation = useMutation();
  return (
    <details>
      <summary>{copy.resolve}</summary>
      <form
        onSubmit={(event) =>
          onSubmit(event, () =>
            mutation.run(async () => {
              const result = await request(
                botPath(
                  scope,
                  `conversations/${conversationId}/holds/${holdId}${operation === 'resolve' ? '' : '/triage'}`
                ),
                z.union([savedSchema, z.object({ id: z.string().nullable() })]),
                {
                  csrf: session.csrf,
                  body:
                    operation === 'resolve'
                      ? { reference, note, caseId: sourceCaseId }
                      : operation === 'create_case'
                        ? { operation, reference, note }
                        : { operation, note },
                }
              );
              if ('id' in result && result.id) {
                setOperation('resolve');
              }
              reload();
            })
          )
        }
      >
        {reason !== 'source_unavailable' && (
          <Field label={copy.holdReviewAction}>
            <select
              value={operation}
              onChange={(event) => setOperation(event.target.value)}
            >
              <option value="resolve">{copy.resolve}</option>
              <option value="create_case">{copy.createReviewCase}</option>
              <option value="correct_false_positive">
                {copy.correctFalsePositive}
              </option>
            </select>
          </Field>
        )}
        {operation === 'create_case' && (
          <p className="small">{copy.caseHoldRetained}</p>
        )}
        {operation === 'correct_false_positive' && (
          <p className="small">{copy.falsePositiveRule}</p>
        )}
        {operation !== 'correct_false_positive' && (
          <Field label={copy.reference}>
            <input
              required
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          </Field>
        )}
        <Field label={copy.resolutionNote}>
          <textarea
            required
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
        {operation === 'resolve' && (
          <Field label={copy.caseId}>
            <input readOnly value={sourceCaseId ?? ''} />
          </Field>
        )}
        <ErrorNotice error={mutation.error} copy={copy} />
        <button disabled={mutation.busy}>
          {operation === 'resolve'
            ? copy.resolve
            : operation === 'create_case'
              ? copy.createReviewCase
              : copy.correctFalsePositive}
        </button>
      </form>
    </details>
  );
}
